import { useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api';

const UNITS = ['ml', 'cl', 'l', 'g', 'kg', 'unidad', 'botella', 'racion'];

/* Etiquetas de las categorías de la carta (para el filtro) */
const CAT_LABELS = {
  cocktails: 'Cocktails', copas: 'Licores', refrescos: 'Refrescos',
  cervezas_botella: 'Cervezas', cervezas_grifo: 'De grifo', vinos: 'Vinos', zumos: 'Zumos',
  desayunos: 'Desayunos', aperitivos: 'Aperitivos', raciones: 'Raciones',
  hamburguesas: 'Hamburguesas', asados: 'Asados', comida: 'Comida', postres: 'Postres',
  eventos: 'Eventos', cervezas: 'Cervezas', sin_alcohol: 'Sin alcohol',
};
const catLabel = (c) => CAT_LABELS[c] || c;

const REASON_LABELS = {
  sale: 'Venta', restock: 'Reposición', adjustment: 'Ajuste',
  waste: 'Merma', cancel: 'Anulación', initial: 'Stock inicial',
};

function fmt(n) {
  const v = Number(n || 0);
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
}

/* ── Modal alta/edición de ingrediente ── */
function IngredientModal({ ingredient, onClose, onSaved }) {
  const editing = Boolean(ingredient);
  const [form, setForm] = useState(ingredient ? {
    name: ingredient.name, unit: ingredient.unit,
    lowStockThreshold: String(ingredient.lowStockThreshold ?? 0),
    note: ingredient.note || '', active: ingredient.active,
    stock: String(ingredient.stock ?? 0),
  } : { name: '', unit: 'unidad', lowStockThreshold: '0', note: '', active: true, stock: '0' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      if (editing) {
        await api.patch(`/admin/inventory/ingredients/${ingredient._id}`, {
          name: form.name, unit: form.unit,
          lowStockThreshold: Number(form.lowStockThreshold) || 0,
          note: form.note, active: form.active,
        });
      } else {
        await api.post('/admin/inventory/ingredients', {
          name: form.name, unit: form.unit,
          lowStockThreshold: Number(form.lowStockThreshold) || 0,
          note: form.note, active: form.active,
          stock: Number(form.stock) || 0,
        });
      }
      onSaved();
    } catch (e2) { setError(e2.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="np-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="np-modal inv-modal">
        <div className="np-modal-head">
          <h3>{editing ? 'Editar ingrediente' : 'Nuevo ingrediente'}</h3>
          <button type="button" className="np-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit} className="np-form">
          <div className="np-fields">
            <div className="np-field-full">
              <label>Nombre *</label>
              <input value={form.name} onChange={(e) => set('name', e.target.value)} required placeholder="Ej. Ron Blanco" />
            </div>
            <div className="np-field-half">
              <label>Unidad</label>
              <select value={form.unit} onChange={(e) => set('unit', e.target.value)}>
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div className="np-field-half">
              <label>Umbral de aviso ({form.unit})</label>
              <input type="number" step="0.01" min="0" value={form.lowStockThreshold} onChange={(e) => set('lowStockThreshold', e.target.value)} />
            </div>
            {!editing && (
              <div className="np-field-half">
                <label>Stock inicial ({form.unit})</label>
                <input type="number" step="0.01" value={form.stock} onChange={(e) => set('stock', e.target.value)} />
              </div>
            )}
            <div className="np-field-full">
              <label>Nota</label>
              <input value={form.note} onChange={(e) => set('note', e.target.value)} placeholder="Proveedor, formato…" />
            </div>
            <div className="np-checks">
              <label className="np-check">
                <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} />
                <span>Activo</span>
              </label>
            </div>
          </div>
          {error && <p className="np-error">{error}</p>}
          <div className="np-actions">
            <button type="button" className="neon-btn neon-btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="neon-btn neon-btn-primary" disabled={saving}>
              {saving ? 'Guardando…' : editing ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Modal de movimiento (reposición / ajuste / merma) ── */
function MovementModal({ ingredient, onClose, onSaved }) {
  const [type, setType] = useState('restock');   // restock | waste | adjustment
  const [qty, setQty] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    const amount = Number(qty);
    if (!amount || Number.isNaN(amount)) { setError('Cantidad no válida'); return; }
    let delta = amount;
    let reason = type;
    if (type === 'restock') delta = Math.abs(amount);
    else if (type === 'waste') { delta = -Math.abs(amount); reason = 'waste'; }
    else reason = 'adjustment'; // ajuste: respeta el signo introducido
    setSaving(true); setError('');
    try {
      await api.post(`/admin/inventory/ingredients/${ingredient._id}/movement`, { delta, reason, note });
      onSaved();
    } catch (e2) { setError(e2.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="np-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="np-modal inv-modal">
        <div className="np-modal-head">
          <h3>Movimiento · {ingredient.name}</h3>
          <button type="button" className="np-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit} className="np-form">
          <p className="inv-current">Stock actual: <b>{fmt(ingredient.stock)} {ingredient.unit}</b></p>
          <div className="inv-type-row">
            {[['restock', 'Reposición +'], ['waste', 'Merma −'], ['adjustment', 'Ajuste ±']].map(([v, l]) => (
              <button type="button" key={v} className={`inv-type-btn${type === v ? ' active' : ''}`} onClick={() => setType(v)}>{l}</button>
            ))}
          </div>
          <div className="np-fields">
            <div className="np-field-full">
              <label>Cantidad ({ingredient.unit}){type === 'adjustment' ? ' — usa − para restar' : ''}</label>
              <input type="number" step="0.01" value={qty} onChange={(e) => setQty(e.target.value)} required autoFocus placeholder="0" />
            </div>
            <div className="np-field-full">
              <label>Nota</label>
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Motivo / albarán…" />
            </div>
          </div>
          {error && <p className="np-error">{error}</p>}
          <div className="np-actions">
            <button type="button" className="neon-btn neon-btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="neon-btn neon-btn-primary" disabled={saving}>{saving ? 'Aplicando…' : 'Aplicar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Drawer de historial ── */
function HistoryDrawer({ ingredient, onClose }) {
  const [moves, setMoves] = useState(null);
  useEffect(() => {
    api.get(`/admin/inventory/ingredients/${ingredient._id}/movements`).then(setMoves).catch(() => setMoves([]));
  }, [ingredient._id]);

  return (
    <div className="np-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="inv-drawer">
        <div className="np-modal-head">
          <h3>Historial · {ingredient.name}</h3>
          <button type="button" className="np-close" onClick={onClose}>✕</button>
        </div>
        <div className="inv-history">
          {moves === null && <p className="inv-muted">Cargando…</p>}
          {moves && moves.length === 0 && <p className="inv-muted">Sin movimientos.</p>}
          {moves && moves.map((m) => (
            <div key={m._id} className="inv-move">
              <span className={`inv-move-delta ${m.delta < 0 ? 'neg' : 'pos'}`}>{m.delta > 0 ? '+' : ''}{fmt(m.delta)}</span>
              <div className="inv-move-body">
                <span className="inv-move-reason">{REASON_LABELS[m.reason] || m.reason}</span>
                {m.note && <span className="inv-move-note">{m.note}</span>}
                <span className="inv-move-date">{new Date(m.createdAt).toLocaleString('es-ES')} · saldo {fmt(m.balanceAfter)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Página principal ── */
export default function AdminInventory() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [onlyLow, setOnlyLow] = useState(false);
  const [cat, setCat] = useState('all');
  const [editModal, setEditModal] = useState(null);   // null | 'new' | ingredient
  const [moveModal, setMoveModal] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);

  const load = () => api.get('/admin/inventory/ingredients').then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const lowCount = useMemo(() => items.filter((i) => Number(i.stock) <= Number(i.lowStockThreshold)).length, [items]);

  // Categorías de carta presentes en el inventario (para el desplegable de filtro)
  const presentCats = useMemo(() => {
    const set = new Set();
    items.forEach((i) => (i.categories || []).forEach((c) => set.add(c)));
    return [...set].sort();
  }, [items]);

  const filtered = items.filter((i) => {
    if (onlyLow && !(Number(i.stock) <= Number(i.lowStockThreshold))) return false;
    if (cat !== 'all' && !(i.categories || []).includes(cat)) return false;
    if (search) return i.name.toLowerCase().includes(search.toLowerCase());
    return true;
  });

  const del = async (i) => {
    if (!confirm(`¿Eliminar "${i.name}"?`)) return;
    try { await api.del(`/admin/inventory/ingredients/${i._id}`); load(); }
    catch (e) { alert(e.message); }
  };

  const afterSave = () => { setEditModal(null); setMoveModal(null); load(); };

  return (
    <div className="neon-admin-page">
      <div className="neon-admin-page-head">
        <div>
          <h2>Inventario</h2>
          <p className="neon-admin-subtitle">
            {items.length} ingredientes · {lowCount > 0 ? <span className="inv-low-text">{lowCount} bajo stock</span> : 'todo en orden'}
          </p>
        </div>
        <div className="neon-admin-head-right">
          <input className="np-search" placeholder="Buscar ingrediente…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <label className="np-inactive-toggle">
            <input type="checkbox" checked={onlyLow} onChange={(e) => setOnlyLow(e.target.checked)} />
            <span>Solo bajo stock</span>
          </label>
          <button type="button" className="neon-btn neon-btn-primary np-btn-new" onClick={() => setEditModal('new')}>+ Nuevo</button>
        </div>
      </div>

      {/* Filtro por sección de la carta — pestañas como en la carta */}
      <div className="np-cat-rail" role="tablist" aria-label="Filtrar por sección de la carta">
        <button
          type="button"
          role="tab"
          aria-selected={cat === 'all'}
          className={`np-cat-btn${cat === 'all' ? ' active' : ''}`}
          onClick={() => setCat('all')}
        >
          Todas <span className="np-cat-count">{items.length}</span>
        </button>
        {presentCats.map((c) => {
          const count = items.filter((i) => (i.categories || []).includes(c)).length;
          return (
            <button
              key={c}
              type="button"
              role="tab"
              aria-selected={cat === c}
              className={`np-cat-btn${cat === c ? ' active' : ''}`}
              onClick={() => setCat(c)}
            >
              {catLabel(c)} <span className="np-cat-count">{count}</span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="neon-orders-empty">No hay ingredientes con los filtros seleccionados.</div>
      ) : (
        <div className="inv-table">
          <div className="inv-row inv-head">
            <span>Ingrediente</span><span>Stock</span><span>Umbral</span><span>Estado</span><span>Secciones</span><span className="inv-actions-col">Acciones</span>
          </div>
          {filtered.map((i) => {
            const low = Number(i.stock) <= Number(i.lowStockThreshold);
            return (
              <div key={i._id} className={`inv-row${low ? ' inv-row-low' : ''}${i.active ? '' : ' inv-row-off'}`}>
                <span className="inv-name">{i.name}{!i.active && <em className="inv-off-tag">inactivo</em>}</span>
                <span className="inv-stock">{fmt(i.stock)} <small>{i.unit}</small></span>
                <span className="inv-thr">{fmt(i.lowStockThreshold)} <small>{i.unit}</small></span>
                <span>{low ? <span className="inv-badge low">Bajo</span> : <span className="inv-badge ok">OK</span>}</span>
                <span className="inv-cats">
                  {(i.categories || []).length === 0
                    ? <em className="inv-off-tag">—</em>
                    : (i.categories || []).map((c) => <span key={c} className="inv-cat-chip">{catLabel(c)}</span>)}
                </span>
                <span className="inv-actions">
                  <button type="button" className="neon-btn neon-btn-secondary inv-mini" onClick={() => setMoveModal(i)}>Movimiento</button>
                  <button type="button" className="inv-icon-btn" title="Historial" onClick={() => setHistoryOf(i)}>🕑</button>
                  <button type="button" className="inv-icon-btn" title="Editar" onClick={() => setEditModal(i)}>✎</button>
                  <button type="button" className="inv-icon-btn inv-del" title="Eliminar" onClick={() => del(i)}>🗑</button>
                </span>
              </div>
            );
          })}
        </div>
      )}

      {editModal && (
        <IngredientModal ingredient={editModal === 'new' ? null : editModal} onClose={() => setEditModal(null)} onSaved={afterSave} />
      )}
      {moveModal && (
        <MovementModal ingredient={moveModal} onClose={() => setMoveModal(null)} onSaved={afterSave} />
      )}
      {historyOf && (
        <HistoryDrawer ingredient={historyOf} onClose={() => setHistoryOf(null)} />
      )}
    </div>
  );
}
